import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';

const OuterWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  grid-template-rows: 175px 1fr;
  display: grid;
  background: #e8e9ea;

  @media (orientation: portrait) {
    grid-template-rows: 104px 1fr;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-column-gap: 1px;
`;

const ColumnLeft = styled.div`
  height: 100%;
`;

const ColumnRight = styled.div`
  height: 100%;
  background: #fff;
`;

const ItemsLeft = styled.div`
  display: grid;
  grid-gap: 1px;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(4, 1fr);
  grid-auto-rows: 0;
  overflow-y: hidden;
  height: 100%;

  @media (orientation: portrait) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(8, 1fr);
  }
`;

const ItemsRight = styled.div`
  display: grid;
  grid-gap: 1px;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(4, 1fr);
  grid-auto-rows: 0;
  overflow-y: hidden;
  height: 100%;

  @media (orientation: portrait) {
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: repeat(8, 1fr);
  }
`;

const Order = styled.div<{ ready?: boolean; background?: string }>`
  display: grid;
  align-items: center;
  justify-items: center;
  color: ${(props) => (props.ready ? '#fff' : 'rgb(28, 35, 44)')};
  background: ${(props) => (props.ready ? props.background : 'rgb(255, 255, 255)')};
`;

const OrderId = styled.div<{
  ready?: boolean;
}>`
  font-weight: 600;
  line-height: 88px;
  font-size: 80px;
`;

const LabelWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  align-items: center;
  justify-items: center;
  background: #fff;
  padding: 0 15%;
  box-sizing: border-box;

  @media (orientation: portrait) {
    padding: 0 5%;
  }
`;

const Label = styled.div`
  color: rgb(139, 143, 147);
  font-size: 48px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 56px;
  text-align: center;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  align-items: center;
  background: #eeeef1;
  font-family: Eina01-SemiBold;
  font-style: normal;
  font-weight: 600;
  font-size: 56px;
  text-align: center;
  color: #131316;

  @media (orientation: portrait) {
    font-size: 48px;
  }
`;

const HeaderCell = styled.div``;

interface IOrder {
  id: string;
  easyId: string;
}

interface Props {
  cooking: IOrder[];
  ready: IOrder[];
  toggleMenu: () => void;
  brandColor: string | null;
}

const ResponsiveCDS: React.FunctionComponent<Props> = ({
  cooking,
  ready,
  toggleMenu,
  brandColor,
}) => {
  const { i18n } = useI18n();
  const [clickCount, setClickCount] = React.useState(0);
  const clickTimeoutRef = React.useRef<number | null>(null);

  const currentCooking = cooking.filter(
    (r, index, array) => array.findIndex((value) => value.easyId === r.easyId) === index,
  );

  const currentReady = ready.filter((r, index, array) => {
    const hasMultiple = currentCooking.findIndex((value) => value.easyId === r.easyId);
    const duplicate = array.findIndex((value) => value.easyId === r.easyId) === index;

    return hasMultiple == -1 || !duplicate;
  });

  const numberLeftItems = 16;

  // Handle clicks to detect 4 rapid clicks in order to navigate back to gateway - this to prevent accidental clicks
  const handleClick = () => {
    // Clear existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    const newCount = clickCount + 1;
    setClickCount(newCount);

    // If we've reached 4 clicks, toggle the menu
    if (newCount >= 4) {
      toggleMenu();
      setClickCount(0);
    } else {
      // Reset counter after 3 seconds of inactivity
      clickTimeoutRef.current = setTimeout(() => {
        setClickCount(0);
      }, 3000);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  if (currentCooking.length === 0 && currentReady.length === 0) {
    return (
      <OuterWrapper onClick={handleClick}>
        <LabelWrapper>
          <Label>{i18n('CustomerDisplay.FollowOrder')}</Label>
        </LabelWrapper>
      </OuterWrapper>
    );
  }

  return (
    <OuterWrapper>
      <Header>
        <HeaderCell>{i18n('CustomerDisplay.Preparing')}</HeaderCell>
        <HeaderCell>{i18n('CustomerDisplay.Ready')}</HeaderCell>
      </Header>
      <Wrapper onClick={handleClick}>
        <ColumnLeft>
          {currentCooking.length === 0 ? (
            <LabelWrapper>
              <Label>{i18n('CustomerDisplay.KeepAnEyeHere')}</Label>
            </LabelWrapper>
          ) : (
            <ItemsLeft>
              {currentCooking.map((order, i) => (
                <Order key={order.id}>
                  <OrderId>{order.easyId}</OrderId>
                </Order>
              ))}
              {currentCooking.length < numberLeftItems &&
                Array(numberLeftItems - currentCooking.length)
                  .fill([])
                  .map(() => <Order key={Math.random()} />)}
            </ItemsLeft>
          )}
        </ColumnLeft>
        <ColumnRight>
          {currentReady.length === 0 ? (
            <LabelWrapper>
              <Label>{i18n('CustomerDisplay.ReadyMeals')}</Label>
            </LabelWrapper>
          ) : (
            <ItemsRight>
              {currentReady.map((order, i) => (
                <Order key={order.id} ready background={brandColor || 'rgb(32, 153, 70)'}>
                  <OrderId>{order.easyId}</OrderId>
                </Order>
              ))}
            </ItemsRight>
          )}
        </ColumnRight>
      </Wrapper>
    </OuterWrapper>
  );
};

export default ResponsiveCDS;
